import React, { useState } from 'react';
import { ArtifactFilter as ArtifactFilterType, ArtifactType } from '../../types/artifact';
import { useTheme } from '../../contexts/ThemeContext';

interface ArtifactFilterProps {
  filter: ArtifactFilterType;
  onFilterChange: (filter: ArtifactFilterType) => void;
  resultCount?: number;
}

type TypeGroup = {
  label: string;
  types: { type: ArtifactType; emoji: string; label: string }[];
};

const TYPE_GROUPS: TypeGroup[] = [
  {
    label: 'Prose',
    types: [
      { type: ArtifactType.TEXT,     emoji: '📝', label: 'Text' },
      { type: ArtifactType.DOCUMENT, emoji: '📄', label: 'Document' },
      { type: ArtifactType.IDEA,     emoji: '💡', label: 'Idea' },
      { type: ArtifactType.CRITIQUE, emoji: '🔍', label: 'Critique' },
      { type: ArtifactType.SOLUTION, emoji: '✅', label: 'Solution' },
    ],
  },
  {
    label: 'AI',
    types: [
      { type: ArtifactType.PROMPT,   emoji: '✨', label: 'Prompt' },
    ],
  },
  {
    label: 'Visual',
    types: [
      { type: ArtifactType.DIAGRAM,  emoji: '📊', label: 'Diagram' },
      { type: ArtifactType.IMAGE,    emoji: '🖼️', label: 'Image' },
    ],
  },
  {
    label: 'Structured Data',
    types: [
      { type: ArtifactType.JSON,     emoji: '🔧', label: 'JSON' },
      { type: ArtifactType.YAML,     emoji: '⚙️', label: 'YAML' },
      { type: ArtifactType.TOML,     emoji: '🔩', label: 'TOML' },
      { type: ArtifactType.CSV,      emoji: '🗃️', label: 'CSV' },
    ],
  },
  {
    label: 'Dev',
    types: [
      { type: ArtifactType.CODE,     emoji: '💻', label: 'Code' },
    ],
  },
];

const PHASES = [
  { value: 'teamselection', label: 'Team Selection' },
  { value: 'brainstorming',  label: 'Brainstorming' },
  { value: 'critique',       label: 'Critique' },
  { value: 'synthesis',      label: 'Synthesis' },
  { value: 'refinement',     label: 'Refinement' },
];

export const ArtifactFilter: React.FC<ArtifactFilterProps> = ({
  filter,
  onFilterChange,
  resultCount,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { darkMode } = useTheme();

  const hasActiveFilters =
    !!filter.type || !!filter.phase || !!filter.search ||
    !!filter.dateFrom || !!filter.dateTo;

  const handleTypeToggle = (type: ArtifactType) => {
    onFilterChange({ ...filter, type: filter.type === type ? undefined : type });
  };

  const handlePhaseChange = (phase: string | undefined) => {
    onFilterChange({ ...filter, phase });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filter, search: e.target.value });
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filter, dateFrom: e.target.value });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filter, dateTo: e.target.value });
  };

  const handleClearFilters = () => {
    onFilterChange({});
  };

  return (
    <div className={`artifact-filter ${darkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">
          Filters
          {resultCount !== undefined && (
            <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              ({resultCount})
            </span>
          )}
        </h3>
        <button
          className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} text-sm`}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Search
        </label>
        <input
          type="text"
          className={`w-full px-3 py-2 border rounded-md text-sm ${
            darkMode
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
          placeholder="Search artifacts..."
          value={filter.search || ''}
          onChange={handleSearchChange}
        />
      </div>

      {/* Type icon button group — grouped */}
      <div className="mb-4">
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Type
        </label>
        <div className="space-y-2">
          {TYPE_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  {group.label}
                </span>
                <div className={`flex-1 h-px ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
              <div className="grid grid-cols-2 gap-1">
                {group.types.map(({ type, emoji, label }) => {
                  const active = filter.type === type;
                  return (
                    <button
                      key={type}
                      onClick={() => handleTypeToggle(type)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        active
                          ? darkMode
                            ? 'bg-blue-700 border-blue-500 text-white'
                            : 'bg-blue-50 border-blue-400 text-blue-700'
                          : darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{emoji}</span>
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Phase */}
      <div className="mb-4">
        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Phase
        </label>
        <select
          className={`w-full px-3 py-2 border rounded-md text-sm ${
            darkMode
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
          value={filter.phase || ''}
          onChange={e => handlePhaseChange(e.target.value || undefined)}
        >
          <option value="">All Phases</option>
          {PHASES.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      {/* Collapsible date range */}
      <div
        className="overflow-hidden transition-all duration-200"
        style={{ maxHeight: expanded ? '200px' : '0px' }}
      >
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Date From
          </label>
          <input
            type="date"
            className={`w-full px-3 py-2 border rounded-md text-sm ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            value={filter.dateFrom || ''}
            onChange={handleDateFromChange}
          />
        </div>
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Date To
          </label>
          <input
            type="date"
            className={`w-full px-3 py-2 border rounded-md text-sm ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            value={filter.dateTo || ''}
            onChange={handleDateToChange}
          />
        </div>
      </div>

      {/* Conditional clear button */}
      {hasActiveFilters && (
        <button
          className={`w-full px-4 py-2 rounded-md text-sm transition-colors ${
            darkMode
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={handleClearFilters}
        >
          Clear Filters
        </button>
      )}
    </div>
  );
};
